import type {ReactNode} from 'react';
import {Link} from 'react-router';
import {Badge} from '@goproblems/ui';
import {UserIdentity} from '@components/common/UserIdentity';
import {ProblemRating} from '@components/common/ProblemRating';
import {ProblemGenreEnum, type ProblemEntry} from '@types';
import {formatDateString} from '@utils/dateFormatter';

export const DashboardTableDate = ({
    value,
    showTime = false,
}: {
    value?: string | null;
    showTime?: boolean;
}) => {
    if (!value) return <span className="dashboard-table-muted">—</span>;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return <span className="dashboard-table-muted">{value}</span>;
    }
    const hasTime = /[T\s]\d{1,2}:\d{2}/.test(value);
    return (
        <time
            className="dashboard-table-date"
            dateTime={date.toISOString()}
            title={formatDateString(value, {
                dateStyle: 'full',
                ...(hasTime && {timeStyle: 'medium'}),
            })}>
            {formatDateString(value, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            })}
            {showTime && hasTime && (
                <span className="dashboard-table-clock">
                    {formatDateString(value, {
                        hour: '2-digit',
                        minute: '2-digit',
                    })}
                </span>
            )}
        </time>
    );
};

export const DashboardTableItem = ({
    to,
    title,
    imageUrl,
    meta,
    imageSize = 64,
}: {
    to: string;
    title: ReactNode;
    imageUrl?: string;
    meta?: ReactNode;
    imageSize?: number;
}) => (
    <div className="dashboard-table-item">
        {imageUrl && (
            <Link to={to} tabIndex={-1} aria-hidden="true">
                <img
                    src={imageUrl}
                    alt=""
                    width={imageSize}
                    height={imageSize}
                    style={{width: imageSize, height: imageSize}}
                />
            </Link>
        )}
        <div className="dashboard-table-item-info">
            <Link to={to} className="dashboard-table-title">
                {title}
            </Link>
            {meta && <div className="dashboard-table-meta">{meta}</div>}
        </div>
    </div>
);

export const DashboardProblemTableItem = ({
    problem,
}: {
    problem: Pick<
        ProblemEntry,
        'id' | 'imageUrl' | 'genre' | 'difficulty' | 'author'
    > & {rating?: number | null};
}) => {
    return (
        <DashboardTableItem
            to={`/problems/${problem.id}`}
            title={`#${problem.id}`}
            imageUrl={problem.imageUrl}
            meta={
                <>
                    <div className="dashboard-table-problem-details">
                        <span>{problem.difficulty}</span>
                        {problem.genre !== ProblemGenreEnum.LIFE_AND_DEATH && (
                            <Badge bg="secondary" title={problem.genre}>
                                {problem.genre}
                            </Badge>
                        )}
                        <ProblemRating rating={problem.rating} />
                    </div>
                    <UserIdentity
                        user={problem.author}
                        avatarSize={18}
                        className="dashboard-table-author"
                    />
                </>
            }
        />
    );
};
